import React, { useState } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import { postAIManagerSummaryFeedbackApi } from 'api/performanceReview';
import useAsync from 'components/hooks/useAsync';
import { useDispatch } from 'react-redux';
import { setMLSummaryFeedback } from 'reducers/reviewFormSlice';

import { MeshLogo } from 'assets/assetsV1/iconComponents';

import { isNetchexLogin } from 'constants/netChexConstants';

import { Divider } from 'uiToolkitV1/Divider';
import { SkeletonLoader } from 'uiToolkitV1/SkeletonLoader';
import { TooltipInfoV1 } from 'uiToolkitV1/TooltipInfoV1';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

import ManagerSummaryFeedback from '../GuideAndHelpDetails/ManagerSummaryFeedback';
import { SummaryPoints } from './ManagerAISummary';
import { useManagerSummary } from './hooks/dataHooks';

const ManagerAISummarySingle = ({
  reviewId,
  questionId,
  revieweeId,
  goalText,
  goalProgressLevel,
  competencyText,
  competencyDescription = '',
  competencyId,
  goalId,
  feedbackText,
  questionText,
}) => {
  const dispatch = useDispatch();

  const {
    data: pointsData,
    isLoading: pointsIsLoading,
    isSuccess: pointsIsSuccess,
    isError: pointsIsError,
  } = useManagerSummary({
    reviewId,
    empId: revieweeId,
    questionId,
    competencyText,
    competencyId,
    competencyDescription,
    goalText,
    goalProgressLevel,
    goalId,
  });
  const { run: runSaveFeedback } = useAsync();

  const [isProvidedFeedback, setIsProvidedFeedback] = useState(null);

  const onGiveFeedbackHandler = async ({ isLike, responseId }) => {
    try {
      await runSaveFeedback(
        postAIManagerSummaryFeedbackApi({
          feedbackComment: feedbackText,
          questionId,
          summaryPoints: pointsData?.summaryPoints,
          isLike,
          reviewId,
          revieweeId,
          responseId,
        })
      );
      dispatch(
        setMLSummaryFeedback({
          reviewId,
          revieweeId,
          questionId,
          competencyId,
          isLike,
          goalId,
        })
      );
      setIsProvidedFeedback(isLike);
    } catch (e) {
      console.log(e);
    }
  };

  return (
    <Flex flexDir="column" alignItems="stretch" mb={4}>
      <Flex alignItems="center" justifyContent="space-between" pt="1px" px="1px" borderTopRadius="8px">
        <Flex
          justifyContent="space-between"
          p="12px 16px"
          bg="white"
          borderTopRadius="8px"
          flexGrow={1}
          alignItems="flex-start"
        >
          <TextBodyMedium bold>{questionText}</TextBodyMedium>
        </Flex>
      </Flex>

      <Flex justifyContent="space-between" pb="1px" px="1px" borderBottomRadius="8px" mt="1px">
        <Box bg="white" borderBottomRadius="8px" flexGrow={1} p={2}>
          {pointsIsLoading && <SkeletonLoader skeletons={['10px', '20px', '10px', '20px', '10px']} />}
          {pointsIsSuccess && pointsData?.is_success && (
            <Flex p={2}>
              <SummaryPoints summaryPoints={pointsData?.summaryPoints} />
            </Flex>
          )}
          {!pointsIsLoading && (pointsIsError || !pointsData?.is_success) && (
            <TextBodyMedium my={1} mx={2}>
              {pointsData?.errorMessage}
            </TextBodyMedium>
          )}
          {!pointsIsError && pointsData?.is_success && (
            <Flex>
              <ManagerSummaryFeedback
                onGiveFeedbackHandler={onGiveFeedbackHandler}
                isFeedbackEnabled={pointsData?.is_success}
                isProvidedFeedback={isProvidedFeedback}
                reviewId={reviewId}
                questionId={questionId}
                competencyId={competencyId}
                revieweeId={revieweeId}
              />
            </Flex>
          )}
        </Box>
      </Flex>
    </Flex>
  );
};

const ManagerAISummaryMultiQuestions = ({
  pasteHandler,
  questions = [],
  reviewId,
  revieweeId,
  goalText,
  goalProgressLevel,
  competencyText,
  competencyDescription,
  competencyId,
  goalId,
}) => (
  <Flex
    borderRadius="8px"
    bg="linear-gradient(90deg, rgba(0, 142, 240, 0.50) 1.83%, rgba(236, 59, 100, 0.50) 34.86%, rgba(255, 194, 0, 0.50) 65.54%, rgba(92, 196, 97, 0.50) 100%)"
    flexDir="column"
    alignItems="stretch"
    mb={4}
  >
    <Flex alignItems="center" justifyContent="space-between" pt="1px" px="1px" borderTopRadius="8px">
      <Flex
        alignItems="center"
        justifyContent="space-between"
        p="10px 8px"
        bg="white"
        borderTopRadius="8px"
        flexGrow={1}
      >
        <Flex alignItems="center">
          {!isNetchexLogin && <MeshLogo boxSize="20px" mr={2} />}
          <TextBodyMedium bold mr={2}>
            Maven Generated Summary
          </TextBodyMedium>
          <TooltipInfoV1 title="All feedback received will be summarized by our AI algorithm." />
        </Flex>
      </Flex>
    </Flex>

    <Flex alignItems="stretch" flexDir="column" pb="1px" px="1px" borderBottomRadius="8px" mt="1px">
      <Flex bg="white" borderBottomRadius="8px" alignItems="stretch" flexDir="column">
        {questions.map(
          (
            { id, description, feedbackTextHtml, feedbackText, selectedAnswerOptions, isViewOnlyModeEnabled },
            index
          ) => (
            <Box>
              <ManagerAISummarySingle
                // eslint-disable-next-line no-shadow
                singleQuestionPasteHandler={({ feedbackText, feedbackTextHtml }) => {
                  pasteHandler({
                    feedbackText,
                    feedbackTextHtml,
                    questionId1: id,
                    questionIndex1: index,
                    selectedAnswerOptions,
                  });
                }}
                feedbackTextHtml={feedbackTextHtml}
                reviewId={reviewId}
                questionId={id}
                questionText={`${index + 1}. ${description}`}
                revieweeId={revieweeId}
                competencyText={competencyText}
                competencyDescription={competencyDescription}
                competencyId={competencyId}
                isViewOnly={isViewOnlyModeEnabled}
                feedbackText={feedbackText}
                goalText={goalText}
                goalProgressLevel={goalProgressLevel}
                goalId={goalId}
              />
              {index !== questions.length - 1 && <Divider mx={4} />}
            </Box>
          )
        )}
      </Flex>
    </Flex>
  </Flex>
);

export default ManagerAISummaryMultiQuestions;
